import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSettings, subscribeToSettings } from '../firebase/firebase-config';
import "../styles/footer.css";

function Footer() {
  const [settings, setSettings] = useState({
    schoolName: 'MIS International School',
    logo: '',
    address: '123 Education Lane, Knowledge City, India 226001',
    phone: '+91 522 123 4567',
    email: 'info@mis-school.edu',
    footerBgColor: '#0f2244',
    footerTextColor: '#ffffff',
    secondaryColor: '#f59e0b',
    primaryColor: '#1a3a6b',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    linkedin: '',
    copyright: '© 2026 MIS International School. All Rights Reserved.',
    googleMapEmbedUrl: '',
    aboutText: 'Providing quality education and nurturing future leaders since 1995.',
    navLinks: []
  });

  useEffect(() => {
    const loadSettings = async () => {
      const data = await getSettings();
      setSettings(prev => ({ ...prev, ...data }));
    };
    loadSettings();

    const unsubscribe = subscribeToSettings((data) => {
      if (data) setSettings(prev => ({ ...prev, ...data }));
    });
    
    return () => unsubscribe();
  }, []);

  const footerBgColor = settings.footerBgColor || '#0f2244';
  const footerTextColor = settings.footerTextColor || '#ffffff';
  const secondaryColor = settings.secondaryColor || '#f59e0b';
  const primaryColor = settings.primaryColor || '#1a3a6b';
  const schoolName = settings.schoolName || 'MIS International School';

  // Extract just the src URL from the embed code if full iframe is provided
  const getMapSrc = (embedCode) => {
    if (!embedCode) return '';
    const srcMatch = embedCode.match(/src="([^"]+)"/);
    if (srcMatch) return srcMatch[1];
    return embedCode;
  };

  const mapSrc = getMapSrc(settings.googleMapEmbedUrl);

  // Quick Links - Only Home, About, Mandatory Disclosure
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Mandatory Disclosure', path: '/mandatory-disclosure' }
  ];

  return (
    <footer className="footer" style={{ backgroundColor: footerBgColor, color: footerTextColor }}>
      <div className="footer-container">
        {/* Main Footer Grid */}
        <div className="footer-main-grid">
          
          {/* School Info Section */}
          <div className="footer-section school-info">
            {settings.logo && (
              <div className="footer-logo">
                <img src={settings.logo} alt={schoolName} />
              </div>
            )}
            <h2 className="school-name">{schoolName}</h2>
            <p className="school-tagline">{settings.aboutText}</p>
            <div className="contact-details">
              {settings.address && (
                <div className="contact-detail">
                  <span className="detail-icon">📍</span>
                  <span>{settings.address}</span>
                </div>
              )}
              {settings.phone && (
                <div className="contact-detail">
                  <span className="detail-icon">📞</span>
                  <a href={`tel:${settings.phone}`} style={{ color: footerTextColor }}>
                    {settings.phone}
                  </a>
                </div>
              )}
              {settings.email && (
                <div className="contact-detail">
                  <span className="detail-icon">✉️</span>
                  <a href={`mailto:${settings.email}`} style={{ color: footerTextColor }}>
                    {settings.email}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="footer-section quick-links">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links-single">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} style={{ color: footerTextColor }}>
                    <span className="link-arrow">›</span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Map Section */}
          {mapSrc && (
            <div className="footer-section map-section">
              <h3 className="footer-heading">Our Location</h3>
              <div className="footer-map">
                <iframe 
                  src={mapSrc}
                  title="School Location"
                  width="100%"
                  height="220"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          )}

          {/* Social Media Section */}
          <div className="footer-section social-section">
            <h3 className="footer-heading">Connect With Us</h3>
            <div className="social-icons">
              {settings.facebook && (
                <a 
                  href={settings.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-circle"
                  style={{ background: `${footerTextColor}15`, color: footerTextColor }}
                  aria-label="Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                  </svg>
                </a>
              )}
              {settings.instagram && (
                <a 
                  href={settings.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-circle"
                  style={{ background: `${footerTextColor}15`, color: footerTextColor }}
                  aria-label="Instagram"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              )}
              {settings.youtube && (
                <a 
                  href={settings.youtube} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-circle"
                  style={{ background: `${footerTextColor}15`, color: footerTextColor }}
                  aria-label="YouTube"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              )}
              {settings.twitter && (
                <a 
                  href={settings.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-circle"
                  style={{ background: `${footerTextColor}15`, color: footerTextColor }}
                  aria-label="Twitter"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 0021.968-12.305c0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              )}
              {settings.linkedin && (
                <a 
                  href={settings.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-circle"
                  style={{ background: `${footerTextColor}15`, color: footerTextColor }}
                  aria-label="LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.204 0 22.225 0z"/>
                  </svg>
                </a>
              )}
            </div>
            <div className="newsletter-note">
              <p>Follow us for latest updates and events</p>
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="footer-bottom">
          <div className="copyright">
            <p>{settings.copyright || `© 2026 ${schoolName}. All Rights Reserved.`}</p>
          </div>
          <div className="powered-by">
            <p>Powered by <span style={{ color: secondaryColor }}>Jauhari WebTech</span></p>
          </div>
        </div>
      </div>

      <style>{`
        .footer {
          background: ${footerBgColor};
          color: ${footerTextColor};
          padding: 60px 0 20px;
          margin-top: auto;
          position: relative;
        }
        
        .footer:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, ${primaryColor}, ${secondaryColor});
        }
        
        .footer-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
        }
        
        /* Main Footer Grid */
        .footer-main-grid {
          display: grid;
          grid-template-columns: 1.2fr 0.6fr 1.2fr 0.8fr;
          gap: 40px;
          margin-bottom: 50px;
        }
        
        /* Footer Section */
        .footer-section {
          display: flex;
          flex-direction: column;
        }
        
        /* School Info Section */
        .school-info {
          gap: 12px;
        }
        
        .footer-logo {
          margin-bottom: 10px;
        }
        
        .footer-logo img {
          max-width: 80px;
          height: auto;
          object-fit: contain;
        }
        
        .school-name {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 8px 0;
          letter-spacing: -0.5px;
        }
        
        .school-tagline {
          font-size: 13px;
          line-height: 1.6;
          opacity: 0.85;
          margin-bottom: 15px;
        }
        
        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 5px;
        }
        
        .contact-detail {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          line-height: 1.5;
        }
        
        .detail-icon {
          font-size: 16px;
          min-width: 24px;
          opacity: 0.9;
        }
        
        .contact-detail a {
          text-decoration: none;
          transition: color 0.3s ease;
          color: ${footerTextColor};
        }
        
        .contact-detail a:hover {
          color: ${secondaryColor};
        }
        
        /* Quick Links */
        .quick-links {
          gap: 15px;
        }
        
        .footer-heading {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 5px 0;
          letter-spacing: 0.5px;
          position: relative;
          display: inline-block;
          padding-bottom: 10px;
        }
        
        .footer-heading:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 40px;
          height: 3px;
          background: ${secondaryColor};
          border-radius: 2px;
        }
        
        .footer-links-single {
          list-style: none;
          padding: 0;
          margin: 5px 0 0 0;
        }
        
        .footer-links-single li {
          margin-bottom: 12px;
        }
        
        .footer-links-single a {
          color: ${footerTextColor};
          text-decoration: none;
          font-size: 13px;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .footer-links-single a:hover {
          color: ${secondaryColor};
          transform: translateX(5px);
        }
        
        .link-arrow {
          font-size: 16px;
          font-weight: 600;
          transition: transform 0.3s ease;
        }
        
        .footer-links-single a:hover .link-arrow {
          transform: translateX(3px);
        }
        
        /* Map Section */
        .map-section {
          gap: 12px;
        }
        
        .footer-map {
          margin-top: 5px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
        
        .footer-map iframe {
          width: 100%;
          height: 200px;
          border: none;
          display: block;
        }
        
        /* Social Section */
        .social-section {
          gap: 15px;
        }
        
        .social-icons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 5px;
        }
        
        .social-icon-circle {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: all 0.3s ease;
          backdrop-filter: blur(4px);
        }
        
        .social-icon-circle svg {
          width: 20px;
          height: 20px;
          fill: currentColor;
        }
        
        .social-icon-circle:hover {
          transform: translateY(-3px);
          background: ${secondaryColor} !important;
          color: ${footerBgColor} !important;
        }
        
        .newsletter-note {
          margin-top: 10px;
        }
        
        .newsletter-note p {
          font-size: 12px;
          opacity: 0.7;
          margin: 0;
        }
        
        /* Footer Bottom */
        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          padding-top: 25px;
          margin-top: 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          font-size: 13px;
        }
        
        .copyright p {
          margin: 0;
          opacity: 0.7;
        }
        
        .powered-by p {
          margin: 0;
          opacity: 0.8;
        }
        
        /* ========== RESPONSIVE DESIGN ========== */
        
        /* Tablet (768px - 1024px) */
        @media (max-width: 1024px) {
          .footer-main-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
          }
          
          .footer-container {
            padding: 0 20px;
          }
        }
        
        /* Mobile (below 768px) */
        @media (max-width: 768px) {
          .footer {
            padding: 45px 0 20px;
          }
          
          .footer-main-grid {
            grid-template-columns: 1fr;
            gap: 35px;
            text-align: center;
          }
          
          .footer-section {
            align-items: center;
            text-align: center;
          }
          
          .footer-heading:after {
            left: 50%;
            transform: translateX(-50%);
          }
          
          .school-name {
            font-size: 20px;
          }
          
          .school-tagline {
            text-align: center;
          }
          
          .contact-details {
            align-items: center;
          }
          
          .contact-detail {
            justify-content: center;
          }
          
          .footer-links-single {
            text-align: center;
          }
          
          .footer-links-single a {
            justify-content: center;
          }
          
          .footer-links-single a:hover {
            transform: translateX(0);
          }
          
          .footer-map {
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
          }
          
          .footer-map iframe {
            height: 200px;
          }
          
          .social-icons {
            justify-content: center;
          }
          
          .footer-bottom {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }
        }
        
        /* Small Mobile (below 480px) */
        @media (max-width: 480px) {
          .footer-container {
            padding: 0 16px;
          }
          
          .footer-main-grid {
            gap: 30px;
          }
          
          .social-icon-circle {
            width: 40px;
            height: 40px;
          }
          
          .social-icon-circle svg {
            width: 18px;
            height: 18px;
          }
          
          .footer-map iframe {
            height: 180px;
          }
          
          .footer-bottom {
            font-size: 11px;
          }
          
          .school-name {
            font-size: 18px;
          }
          
          .footer-heading {
            font-size: 16px;
          }
        }
        
        /* Ensure map responsive on all devices */
        .footer-map iframe {
          width: 100%;
          height: auto;
          min-height: 180px;
          aspect-ratio: 16 / 9;
        }
        
        /* Logo responsive */
        .footer-logo img {
          width: auto;
          max-height: 60px;
        }
        
        @media (max-width: 768px) {
          .footer-logo img {
            max-height: 50px;
          }
        }
      `}</style>
    </footer>
  );
}

export default Footer;